import React, { useRef } from "react";
import { motion, useSpring } from "framer-motion";

interface GlareCardProps {
  children: React.ReactNode;
  className?: string;
  padding?: number;
  scale?: number;
  glow?: {
    enable: boolean;
    opacity: number;
  };
  rotation?: {
    stiffness: number;
    damping: number;
  }
}

export const GlareCard: React.FC<GlareCardProps> = ({ children, className = "", padding = 0, scale = 1.0, glow = {enable: true, opacity: 0.08}, rotation = {stiffness: 200, damping: 15}}) => {
  const cardRef = useRef<HTMLDivElement>(null);

  // Use springs for smooth rotation
  const rotateX = useSpring(0, rotation);
  const rotateY = useSpring(0, rotation);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Calculate rotation relative to center (max 10° tilt)
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const newRotateX = -((y - centerY) / centerY) * 10;
    const newRotateY = ((x - centerX) / centerX) * 10;
    rotateX.set(newRotateX);
    rotateY.set(newRotateY);

    // Calculate the pointer position as percentages
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;
    // Update the CSS variables for the glare (glow) effect
    cardRef.current.style.setProperty("--m-x", `${percentX}%`);
    cardRef.current.style.setProperty("--m-y", `${percentY}%`);
  };

  const handleMouseLeave = () => {
    rotateX.set(0);
    rotateY.set(0);
    if (cardRef.current) {
      cardRef.current.style.setProperty("--m-x", "50%");
      cardRef.current.style.setProperty("--m-y", "50%");
    }
  };

  return (
    <div className={`flex items-center justify-center p-${padding}`} style={{ perspective: "1000px" }}>
      <motion.div
        ref={cardRef}
        className={`relative group overflow-hidden shadow-lg transform-gpu ${className}`}
        style={{ rotateX, rotateY }}
        whileHover={{ scale: scale }}
        transition={{ type: "spring", stiffness: 250, damping: 10 }}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Glare overlay using original logic */}
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-75"
          style={glow ? {
            background: `radial-gradient(farthest-corner circle at var(--m-x) var(--m-y), rgba(255,255,255, ${glow.opacity}) 10%, rgba(255,255,255, ${glow.opacity}) 20%, rgba(255,255,255,0) 90%)`,
          } : {}}
        />
        {/* Card content */}
        {children}
      </motion.div>
    </div>
  );
};